import { distinctUntilChanged, map, type Observable } from 'rxjs';
import { reducedStream, reducer } from '@ardoq/rxbeach';
import { isEqual } from '@ardoq/common-helpers';
import type { ArdoqId } from '@ardoq/api-types';
import type { ComponentPreviewState } from '../miniOverview/types';
import { componentPreviewLoaded, loadComponentPreview } from './actions';
import type { ComponentPreviewsState } from './types';

const setPreviewLoading = (
  state: ComponentPreviewsState,
  { componentId }: { componentId: ArdoqId }
): ComponentPreviewsState => {
  if (state[componentId]?.status === 'loaded') return state;

  return {
    ...state,
    [componentId]: { status: 'loading' },
  };
};

const setPreviewState = (
  state: ComponentPreviewsState,
  {
    componentId,
    state: previewState,
  }: { componentId: ArdoqId; state: ComponentPreviewState }
): ComponentPreviewsState => ({
  ...state,
  [componentId]: previewState,
});

const handleLoadComponentPreview = reducer<
  ComponentPreviewsState,
  { componentId: ArdoqId }
>(loadComponentPreview, setPreviewLoading);

const handleComponentPreviewLoaded = reducer<
  ComponentPreviewsState,
  { componentId: ArdoqId; state: ComponentPreviewState }
>(componentPreviewLoaded, setPreviewState);

export const componentPreviews$ = reducedStream<ComponentPreviewsState>(
  'componentPreviews$',
  {},
  [handleLoadComponentPreview, handleComponentPreviewLoaded]
);

const componentPreviewStreams = new Map<
  ArdoqId,
  Observable<ComponentPreviewState | undefined>
>();

export const getComponentPreview$ = (
  componentId: ArdoqId
): Observable<ComponentPreviewState | undefined> => {
  const existing = componentPreviewStreams.get(componentId);
  if (existing) {
    return existing;
  }

  const stream$ = componentPreviews$.pipe(
    map(state => state[componentId]),
    distinctUntilChanged(isEqual)
  );
  componentPreviewStreams.set(componentId, stream$);

  return stream$;
};
